import React from 'react';
import { Modal, SparkModalPortal, Text } from '@spark';
import { mustWait } from '../utils/busy';
import BusyDetails from './BusyDetails';

const MODAL = {
    open: true,
    showSubtitle: false,
    showOverlay: false,
    showScrollBar: false,
    fixedHeight: false,
};

function ConfirmModal({ busy, onUpdateNow, onUpdateAnyway, onScheduleLater, onClose }) {
    return (
        <Modal
            {...MODAL}
            title="Before you update"
            primaryLabel={mustWait(busy) ? 'Update when finished' : 'Update now'}
            primaryDisabled={!busy || Boolean(busy.loading)}
            tertiaryLabel="Schedule for later"
            onPrimaryClick={onUpdateNow}
            onTertiaryClick={onScheduleLater}
            onCloseClick={onClose}
        >
            <div className="platform-upgrader-modal-body">
                <Text as="p" variant="base-regular" color="secondary" className="platform-upgrader-paragraph">
                    This update takes about 10 minutes. During that time the platform is read-only: you can browse it,
                    but you can&apos;t run processes or make changes. To avoid disruption, we recommend scheduling it
                    for a quiet time, such as overnight. Would you like to continue?
                </Text>
                <BusyDetails busy={busy} onUpdateAnyway={onUpdateAnyway} />
            </div>
        </Modal>
    );
}

function WaitingModal({ busy, onCancel, onUpdateAnyway, onClose }) {
    return (
        <Modal
            {...MODAL}
            title="Waiting to update"
            primaryLabel="Keep waiting"
            tertiaryLabel="Cancel update"
            onPrimaryClick={onClose}
            onTertiaryClick={onCancel}
            onCloseClick={onClose}
        >
            <div className="platform-upgrader-modal-body">
                <BusyDetails busy={busy} waiting onUpdateAnyway={onUpdateAnyway} />
            </div>
        </Modal>
    );
}

/**
 * Confirm an immediate update (Figma 0:7863) with what it would wait for: while work runs the update
 * waits for it, unless the Admin chooses Update anyway. With ``waiting`` it shows what an update that
 * already waits is waiting for, to cancel it or stop the wait. One portal serves both, so moving from
 * one to the other never leaves the closing dialog behind the opening one.
 */
export default function BeforeYouUpdateModal({ open, waiting = false, busy, onClose, ...actions }) {
    return (
        <SparkModalPortal open={open} onClose={onClose} width={480}>
            {waiting ? (
                <WaitingModal busy={busy} onClose={onClose} {...actions} />
            ) : (
                <ConfirmModal busy={busy} onClose={onClose} {...actions} />
            )}
        </SparkModalPortal>
    );
}
